// src/portal/contexts/illustrations/components/CoverReferenceSelector.tsx

'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@/shared/utils/cn';

interface AvailableCharacter {
  name: string;
  hasReference: boolean;
}

interface CoverReferenceSelectorProps {
  availableCharacters: AvailableCharacter[];
  selectedCharacters: string[];
  disabled?: boolean;
  onToggle: (characterName: string) => void;
  /** Heading label. Defaults to the cover-reference wording. */
  label?: string;
  /** Hint shown when nothing is selected (cover = "auto pick"). Omit to hide. */
  autoHint?: string;
  /** Testid prefix so page/cover instances stay distinguishable. */
  testIdPrefix?: string;
  /**
   * Fold the chips away behind a one-line summary.
   *
   * A cast of nine is nine checkboxes and two captions — a block taller than
   * the cover it belongs to, for a setting most people never touch. Folded, it
   * says who is on the cover in one line and opens when somebody wants to
   * change that.
   */
  collapsible?: boolean;
}

/**
 * Shared character multi-select — same chip markup for the cover reference AND
 * the per-page character list. NO selection limit (the enforced cap is
 * server-side); callers just pass the candidate names + current selection.
 */
export function CoverReferenceSelector({
  availableCharacters,
  selectedCharacters,
  disabled,
  onToggle,
  label,
  autoHint,
  testIdPrefix = 'illustration-editor-cover-reference',
  collapsible,
}: CoverReferenceSelectorProps) {
  const t = useTranslations('illustrations.editor.pageCard');

  if (availableCharacters.length === 0) {
    return null;
  }

  const heading = label ?? t('coverReference');

  const chips = (
    <>
      <div className="flex flex-wrap gap-1.5">
        {availableCharacters.map((char) => {
          const isSelected = selectedCharacters.includes(char.name);

          return (
            <label
              key={char.name}
              className={cn(
                'flex items-center gap-1.5 rounded-md border px-1.5 py-0.5 text-xs transition-colors',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                isSelected && !disabled && 'bg-primary/10 border-primary',
                !isSelected && !disabled && 'hover:bg-muted'
              )}
            >
              <input
                type="checkbox"
                checked={isSelected}
                disabled={disabled}
                onChange={() => onToggle(char.name)}
                className="h-3.5 w-3.5 rounded border-border"
                data-testid={`${testIdPrefix}-checkbox-${char.name}`}
              />
              <span>{char.name}</span>
              {!char.hasReference && (
                <span className="text-[10px] text-muted-foreground">({t('noRef')})</span>
              )}
            </label>
          );
        })}
      </div>
      {selectedCharacters.length === 0 && autoHint !== undefined && (
        <p className="mt-1.5 text-xs text-muted-foreground">{autoHint}</p>
      )}
    </>
  );

  if (collapsible) {
    const names = selectedCharacters.length > 0
      ? selectedCharacters.join(', ')
      : (autoHint ?? '');
    return (
      <details className="rounded-md border px-2 py-1.5" data-testid={`${testIdPrefix}-selector`}>
        <summary className="cursor-pointer list-none text-xs text-muted-foreground marker:hidden">
          <span className="font-medium text-foreground">{heading}</span>
          {names && <span className="ml-1 line-clamp-1">{names}</span>}
        </summary>
        <div className="mt-2">{chips}</div>
      </details>
    );
  }

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-selector`}>
      <label className="text-sm text-muted-foreground">{heading}:</label>
      {chips}
      {selectedCharacters.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {t('coverReferenceSelectedMultiple', { count: selectedCharacters.length })}
        </p>
      )}
    </div>
  );
}
